//NODE MODULES
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router";
import { useAtomValue } from "jotai";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

//GRAPHQL
import { useLazyQuery, gql } from "@apollo/client";

//KPP COMPONENTS
import { PageHeader, MenuButton } from "../components/helpers";
import { ProjectItem } from "../components/projects";
import { Sector } from "../components/home";

//ASSETS
import allSectorImg from "../assets/img/hero/feature-01.jpg";
import SectorDialog from "../components/projects/SectorDialog";

//STATE
import { previewContentAtom } from "../state/appState";

//GRAPHQL
const QUERY_LIMIT = 18;
const STATIC_QUERY = gql`
	query Sectors($sectorSlug: String!, $isAll: Boolean!, $isPreview: Boolean = false) {
		sectorList: sectorCollection(limit: 12, preview: $isPreview, order: [priority_ASC]) {
			items {
				sys {
					id
				}
				name
				image {
					url
				}
				slug
			}
		}
		sector: sectorCollection(limit: 1, preview: $isPreview, where: { slug: $sectorSlug }) @skip(if: $isAll) {
			items {
				name
				summary
				summaryRich {
					json
				}
			}
		}
	}
`;
const DYNAMIC_QUERY = gql`
	query Projects($sectorSlug: String!, $isAll: Boolean!, $isPreview: Boolean = false, $queryLimit: Int, $querySkip: Int) {
		allProjects: projectCollection(
			limit: $queryLimit
			where: { OR: [{ hideFromAllProjects: false }, { hideFromAllProjects_exists: false }] }
			order: [featuredPriority_DESC, completion_DESC, sys_firstPublishedAt_DESC]
			skip: $querySkip
			preview: $isPreview
		) @include(if: $isAll) {
			total
			items {
				sys {
					id
					firstPublishedAt
				}
				title
				slug
				completion
				location
				heroImage {
					url
				}
			}
		}
		sectorProjects: projectCollection(
			limit: $queryLimit
			where: { sector: { slug: $sectorSlug } }
			order: [featuredPriority_DESC, completion_DESC, sys_firstPublishedAt_DESC]
			skip: $querySkip
			preview: $isPreview
		) @skip(if: $isAll) {
			total
			items {
				sys {
					id
					firstPublishedAt
				}
				title
				slug
				completion
				location
				heroImage {
					url
				}
			}
		}
	}
`;

const Projects = () => {
	const observer = useRef(null);
	const urlParams = useParams();
	const sectorBttnRef = useRef();
	const loadMoreTrigger = useRef();

	//Local State
	const [header, setHeader] = useState();
	const [projects, setProjects] = useState([]);
	const [querySkip, setQuerySkip] = useState(0);
	const [showMore, setShowMore] = useState(false);

	//Atom State
	const isPreviewContent = useAtomValue(previewContentAtom);

	//GraphQL Setup
	const [getStatic, { loading: staticLoading, error: staticError, data: staticData }] = useLazyQuery(STATIC_QUERY, {
		variables: {
			sectorSlug: urlParams.sectorId,
			isAll: urlParams.sectorId === "all" ? true : false,
			isPreview: isPreviewContent,
		},
	});
	const [getProjects, { loading: dynamicLoading, error: dynamicError, data: dynamicData }] = useLazyQuery(DYNAMIC_QUERY, {
		variables: {
			sectorSlug: urlParams.sectorId,
			isAll: urlParams.sectorId === "all" ? true : false,
			isPreview: isPreviewContent,
			queryLimit: QUERY_LIMIT,
			querySkip: querySkip,
		},
	});

	useEffect(() => {
		setHeader(null);
		setQuerySkip(0);
		setShowMore(false);
		setProjects([]);
		getStatic();
		getProjects();
		document.getElementById("sectorsMenu")?.hidePopover();
	}, [urlParams]);

	useEffect(() => {
		let headerData = { title: "All Projects", desc: "" };

		if (staticData?.sector) {
			headerData = {
				title: staticData.sector.items[0].name,
				desc: staticData.sector.items[0].summaryRich?.json,
			};
		}

		setHeader(headerData);
	}, [staticData]);

	useEffect(() => {
		let projectsData = [];

		if (dynamicData?.allProjects) {
			projectsData = dynamicData.allProjects.items;
		} else if (dynamicData?.sectorProjects) {
			projectsData = dynamicData.sectorProjects.items;
		}

		if (dynamicData && projectsData.length < QUERY_LIMIT) {
			setShowMore(false);
			if (loadMoreTrigger.current) observer?.current?.unobserve(loadMoreTrigger.current);
		} else if (dynamicData) {
			setShowMore(true);
		}

		setProjects((prevState) => [...prevState, ...projectsData]);
	}, [dynamicData]);

	//Use this to automatically load more projects when hitting the bottom of the page
	useEffect(() => {
		if (showMore) {
			//create new instance and pass a callback function
			observer.current = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						const intersecting = entry.isIntersecting;
						if (intersecting) {
							getProjects();
							setQuerySkip((prevState) => prevState + QUERY_LIMIT);
						}
					});
				},
				{ rootMargin: "-200px" }
			);

			//Get the trigger element and observe it
			observer.current.observe(loadMoreTrigger.current);
		}

		//Cleanup function to remove observer
		return () => {
			if (loadMoreTrigger.current) observer?.current?.unobserve(loadMoreTrigger.current);
		};
	}, [showMore]);

	//Use this to manually load more projects
	const loadMore = () => {
		setQuerySkip((prevState) => prevState + QUERY_LIMIT);
		getProjects();
	};

	return (
		<>
			<PageHeader />
			<div className="grid pageHeader-wrapper aItems-center">
				<div id="pageHeader-title" className="aSelf-start">
					<h1>{header && header.title}</h1>
				</div>
				<div id="pageHeader-desc">
					<p>{header && documentToReactComponents(header.desc)}</p>
				</div>
				{staticData && (
					<>
						<SectorDialog allSectorImg={allSectorImg} sectorList={staticData.sectorList} />
						{/* <MenuButton id="sectorsMenu" label="View By Sector">
							<div className="grid-wrapper grid-columns-autoFill projectSector-wrapper">
								<Sector sectorTitle="All Sectors" slug={"all"} image={allSectorImg} />
								{staticData.sectorList.items.map((sector) => (
									<Sector key={sector.sys.id} sectorTitle={sector.name} slug={sector.slug} image={sector.image.url} />
								))}
							</div>
						</MenuButton> */}
					</>
				)}
			</div>

			{/************************************
			 * COMBINED PROEJCTS VERSION
			 ***********************************/}
			<div className="grid-wrapper grid-columns-autoFill projects-wrapper">
				{projects.length ? (
					projects.map((project, idx) => {
						const delay = 200 * (idx / ((querySkip + QUERY_LIMIT) / QUERY_LIMIT) + 1);
						return (
							<ProjectItem
								key={`project-${project.sys.id}`}
								id={project.sys.id}
								title={project.title}
								slug={project.slug}
								image={project.heroImage?.url}
								completed={project.completion}
								location={project.location}
								animationDelay={delay}
							/>
						);
					})
				) : (
					<p>There are no projects for this sector</p>
				)}
			</div>

			{/* {showMore && (
				<button type="button" onClick={() => loadMore()} className="marginBlock-medium marginInline-auto">
					Load More
				</button>
			)} */}

			{dynamicLoading && <div className="loadingSpinner"></div>}
			{showMore && <div id="loadMore" ref={loadMoreTrigger}></div>}
		</>
	);
};

export default Projects;
